import { ViewMode } from '@playbooks/action-cards-utils'
import { Section } from '@playbooks/core/src/interfaces/ActionCard'
import { cn } from '@playbooks/ui/lib/utils'
import sanitizedHtml from '../utils/sanitizeHtml'

import {
  MultilevelAccordionContent,
  MultilevelAccordionItem,
  MultilevelAccordionTrigger,
} from './ui/MultilevelAccordion'
import Score from './Score'
import { processReplacements } from '../utils/processReplacements'
import { useAppContext } from '../contexts/AppContext'
import { useRef, useState } from 'react'
import scrollIntoView from '../utils/scrollIntoView'

/**
 * SectionWrapper component wraps a section with accordion functionality.
 * It uses the MultilevelAccordionItem component to create an accordion item for each section.
 * The trigger of the accordion displays the section name and score, and the content displays the section details.
 * @param props - The props for the SectionWrapper component, including the section data and children components.
 * @returns A React component that renders a section wrapped in an accordion item.
 */
export default function SectionWrapper({
  children,
  section,
  isPinnedView,
}: {
  children: React.ReactNode
  section: Section
  isPinnedView?: boolean
}) {
  const [isSectionOpened, setIsSectionOpened] = useState(true)
  const sectionRef = useRef<HTMLDivElement>(null)

  const {
    settings: { viewMode, isSplitViewEnabled },
    update,
    data,
    elementsWithCode,
  } = useAppContext()

  const isFunnelView = viewMode === ViewMode.FUNNEL
  const isDocView = viewMode === ViewMode.DOC
  const isUiSimple = data.isUiSimple && !isFunnelView

  const sectionHint = processReplacements(section.hint, elementsWithCode)
  const sectionName = isFunnelView ? undefined : isUiSimple ? section.hint || section.name : section.name

  const handleSectionOpenClose = () => {
    // In funnel view, sections are not collapsible, so we do nothing on click
    if (isFunnelView) return

    const scrollContainer = sectionRef.current?.closest<HTMLDivElement>(
      '[data-role="data-action-cards-sides-scroll-container"]',
    )

    const isOpened = isPinnedView ? isSectionOpened : !!section.isOpened

    if (!isOpened && sectionRef.current && scrollContainer) {
      scrollIntoView({
        scrollIntoViewElement: sectionRef.current,
        scrollContainerRef: scrollContainer,
      })
    }

    if (isPinnedView) {
      setIsSectionOpened(prev => !prev)
      return
    }

    update(section, 'isOpened', !section.isOpened)
  }

  const isOpened = isPinnedView ? isSectionOpened : isFunnelView || isDocView || !!section.isOpened

  const isColorSet = !!section.color.color

  return (
    <div
      ref={sectionRef}
      data-section-id={section.id}
      key={section.id}
      data-role="section-wrapper"
      className={cn(
        'group-data-[view=default]/app:rounded-md',
        'group-data-[view=default]/app:border',
        'group-data-[view=default]/app:bg-card',
        'border-color-custom-border/50 overflow-hidden mb-2 font-normal',
        !section.isOpened && data.isUiSimple ? 'min-h-9.25' : '',
        section.isPinned && isSplitViewEnabled && !isPinnedView && 'hidden',
      )}
    >
      <MultilevelAccordionItem
        open={!!isOpened}
        className={cn(
          'relative group-data-[view=default]/app:border-l-8',
          !isColorSet && [isUiSimple ? 'group-data-[view=default]/app:pt-9' : 'group-data-[view=default]/app:pt-12'],
        )}
        style={{ borderLeftColor: section.color.color || 'transparent' }}
        renderTrigger={({ isOpen }) => (
          <MultilevelAccordionTrigger
            hideChevron={isFunnelView || isDocView}
            isOpen={isOpen}
            onClick={handleSectionOpenClose}
            className={cn(
              'group-data-[view=funnel]/app:hidden',
              'group-data-[view=default]/app:bg-accent',
              'group-data-[view=default]/app:border-color-custom-border/50]',
              'group-data-[view=default]/app:bg-input/38',
              'group-data-[view=default]/app:dark:bg-input/75',
              'group-data-[view=default]/app:border-color-custom-border/50',
              'group-data-[view=default]/app:border-b',
              'group-data-[view=default]/app:min-h-12',

              'group-data-[view=default]/app:min-h-12',
              !isFunnelView && 'cursor-pointer',
              isUiSimple
                ? 'group-data-[view=default]/app:min-h-9 flex-row-reverse border-b-transparent bg-background!'
                : '',
              isOpen ? '' : 'border-b-transparent',
              !isColorSet && [
                'group-data-[view=default]/app:absolute',
                'group-data-[view=default]/app:-left-2',
                'group-data-[view=default]/app:top-0',
                'group-data-[view=default]/app:right-0',
                'group-data-[view=default]/app:pl-6',
              ],
            )}
          >
            <div
              className={cn('text-base font-semibold leading-6 mr-auto pl-2', isUiSimple && 'pl-0 font-normal text-sm')}
            >
              {sectionName}
            </div>
            {!isUiSimple && <Score value={section.score} />}
          </MultilevelAccordionTrigger>
        )}
      >
        <MultilevelAccordionContent className="p-4">
          {!isUiSimple && (
            <div className="mb-4" dangerouslySetInnerHTML={{ __html: sanitizedHtml(sectionHint || '') }} />
          )}
          {children}
        </MultilevelAccordionContent>
      </MultilevelAccordionItem>
    </div>
  )
}
